import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "@/packages/utils";
import type { ChatPanelMessagesProps } from "./types";

export function ChatPanelMessages({ messages }: ChatPanelMessagesProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(["chat"]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  });

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <p className="text-[12px] text-muted/70 font-medium">
          {t("assistant.emptyState")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3 slim-scroll">
      {messages.map((message) => (
        <motion.div
          key={message.id}
          className={`flex flex-col ${message.role === "user" ? "items-end" : "items-start"}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          <div
            className={
              message.role === "user"
                ? "bg-accent text-white rounded-2xl rounded-br-md px-4 py-2.5 max-w-[85%] shadow-sm"
                : "bg-white/70 border border-border/30 text-primary rounded-2xl rounded-bl-md px-4 py-2.5 max-w-[85%]"
            }
          >
            <p className="text-[13px] leading-relaxed whitespace-pre-wrap">
              {message.content}
            </p>
          </div>
          {message.timestamp && (
            <span className="text-[10px] text-muted/50 mt-1 px-1 font-medium">
              {message.timestamp}
            </span>
          )}
        </motion.div>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}
